import { useCallback, useLayoutEffect } from 'react';
import {
  useAppState,
  useAsyncStorage,
  useSafeFocusEffect,
  useSafeIsFocused,
  useSafeNavigation,
  useSafeRouter,
} from '@sg/garnish';
import { OrderPaymentStatus } from '@sg/graphql-schema';
import { OrderTypeValues } from '@sg/shared-be/features/ordering';

import {
  getOrderPaymentStateConfig,
  isOrderPaymentStatePending,
  OrderPaymentStateKey,
} from '@order/features/ordering';
import { useFeatureFlag } from '@order/LaunchDarkly';
import { useIsExpoRouterEnabled } from '@order/shared/hooks';
import { useTelemetry } from '@order/Telemetry';

import { useIsLoggedIn } from '../../context/AuthMachine';
import { useOrderPaymentStateQuery } from './GraphQL/OrderPaymentState.generated';

// ─────────────────────────────────────────────────────────────────────────────

export const useInitialOrderPaymentState = () => {
  const isExpoRouterEnabled = useIsExpoRouterEnabled();
  const navigation = useSafeNavigation();
  const router = useSafeRouter();
  const isFocused = useSafeIsFocused();
  const isLoggedIn = useIsLoggedIn();
  const { track } = useTelemetry();

  const isIntentPollingEnabled = useFeatureFlag('CELS-4828-order-payment-state-polling-enabled'); // prettier-ignore
  const orderPaymentStateConfig = useFeatureFlag('CELS-4828-order-payment-state-config'); // prettier-ignore
  const { paymentStateExpirationTolerance } = getOrderPaymentStateConfig(orderPaymentStateConfig); // prettier-ignore

  // ─── Local Storage ─────────────────────────────────────────────────────────
  // Here we rely on local storage instead of cart state because the cart might no longer exist.

  const [orderId = '', _, refetchOrderId] =
    useAsyncStorage(OrderPaymentStateKey);

  // ─── Queries ───────────────────────────────────────────────────────────────

  const [orderPaymentState, refetchOrderPaymentState] =
    useOrderPaymentStateQuery({
      pause: !orderId,
      requestPolicy,
      variables: { orderId, orderType },
    });

  const { status, updatedAt } = orderPaymentState.data?.orderPaymentState ?? {};
  const isLoadingOrderPaymentState =
    orderPaymentState.fetching || orderPaymentState.stale;

  const fetchOrderPaymentState = useCallback(async () => {
    const hasOrderId = await refetchOrderId();
    if (!isLoggedIn || !isIntentPollingEnabled || !hasOrderId) return;
    refetchOrderPaymentState();
  }, [
    isLoggedIn,
    isIntentPollingEnabled,
    refetchOrderId,
    refetchOrderPaymentState,
  ]);

  const redirectIfNeeded = useCallback(async () => {
    if (!isFocused) return;
    if (!isLoggedIn) return;
    if (!isIntentPollingEnabled) return;
    if (isLoadingOrderPaymentState) return;

    const hasOrderId = await refetchOrderId();
    if (!hasOrderId) return;

    // ─── States ────────────────────────────────────────────────────────────────
    // If it is pending, failed or fulfilled then it is unresolved.
    // Pending means it still requires polling for the payment state.
    // Fulfilled or failed means it still needs to show customer feedback.

    const hasFulfilled = status === OrderPaymentStatus.Fulfilled;
    const hasFailed = status === OrderPaymentStatus.Failed;
    const isPending = isOrderPaymentStatePending(
      status,
      Number(updatedAt),
      paymentStateExpirationTolerance,
    );

    track('checkout.stripe-usage', {
      event: 'home_order_payment_state',
      isPending,
      hasFulfilled,
      hasFailed,
    });

    if (hasFulfilled || hasFailed || isPending) {
      track('checkout.stripe-usage', { event: 'home_redirect_to_bag' });

      if (isExpoRouterEnabled) {
        router?.navigate('/bag');

        return;
      }

      navigation?.navigate('Modal', { screen: 'Bag' });
    }
  }, [
    isFocused,
    isLoggedIn,
    isIntentPollingEnabled,
    isLoadingOrderPaymentState,
    isExpoRouterEnabled,
    refetchOrderId,
    status,
    updatedAt,
    paymentStateExpirationTolerance,
    track,
    navigation,
    router,
  ]);

  // ─── Refetch On Focus ──────────────────────────────────────────────────────
  // On native, the app will be refocused after the cash app redirect.
  // We must refetch to check if there is a pending payment.

  useAppState({ onVisible: fetchOrderPaymentState });
  useSafeFocusEffect(
    useCallback(() => {
      void fetchOrderPaymentState();
    }, [fetchOrderPaymentState]),
  );

  // ─── Navigate To Bag ───────────────────────────────────────────────────────
  // When there is an unresolved payment, we need to navigate to the bag for polling.

  useLayoutEffect(() => {
    redirectIfNeeded();
  }, [redirectIfNeeded]);
};

// ─── Constants ───────────────────────────────────────────────────────────────

const requestPolicy = 'network-only' as const;
const orderType = OrderTypeValues.GravyOrder;
